import { useAppDispatch, useAppSelector } from "app/hooks"
import { ISetGoalPayload } from "./types"
import {
  onboarding,
  resetOnboardingState,
  getGoal,
  getUser,
  setIsTutorialShowed,
  turnOffWeightChangeNotification,
  setTutorialBackPath,
} from "./onboardingSlice"
import { useEffect } from "react"
import { useFailed } from "features/notification/hooks"
import { isNotNullNotUndefined } from "utils/validator"
import { isGoalWeightProximityError } from "utils/weightValidator"

export const useOnboarding = (callback: () => void) => {
  const onboardingLoading = useAppSelector(
    (state) => state.onboarding.onboardingLoading,
  )
  const onboardingSuccess = useAppSelector(
    (state) => state.onboarding.onboardingSuccess,
  )
  const onboardingFailed = useAppSelector(
    (state) => state.onboarding.onboardingFailed,
  )

  const dispatch = useAppDispatch()
  const handleOnboarding = (payload: ISetGoalPayload) => {
    dispatch(onboarding(payload))
  }

  useEffect(() => {
    if (onboardingSuccess) {
      callback()
    }
  }, [onboardingSuccess])

  useEffect(() => {
    return () => {
      dispatch(resetOnboardingState())
    }
  }, [])

  useFailed(
    isGoalWeightProximityError(onboardingFailed) ? null : onboardingFailed,
  )

  return {
    onboardingLoading,
    onboardingFailed,
    onboardingSuccess,
    handleOnboarding,
  }
}

export const useGoal = () => {
  const getGoalLoading = useAppSelector(
    (state) => state.onboarding.getGoalLoading,
  )
  const getGoalFailed = useAppSelector(
    (state) => state.onboarding.getGoalFailed,
  )
  const goal = useAppSelector((state) => state.onboarding.goal)
  const isNotHasGoalYet = useAppSelector(
    (state) => state.onboarding.isNotHasGoalYet,
  )

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(getGoal(false))
  }, [])

  return {
    getGoalLoading,
    getGoalFailed,
    goal,
    isNotHasGoalYet,
  }
}

export const useUser = () => {
  const getUserLoading = useAppSelector(
    (state) => state.onboarding.getUserLoading,
  )
  const getUserFailed = useAppSelector(
    (state) => state.onboarding.getUserFailed,
  )
  const user = useAppSelector((state) => state.onboarding.user)

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(getUser(false))
  }, [])

  useFailed(getUserFailed, { message: "Get user's info failed!" })

  return {
    getUserLoading,
    getUserFailed,
    user,
  }
}

export const useCheckShouldShowTutorial = () => {
  const user = useAppSelector((state) => state.onboarding.user)
  const loading = useAppSelector((state) => state.onboarding.getUserLoading)
  const isRevalidatedShouldShowTutorial = useAppSelector(
    (state) => state.onboarding.isRevalidatedShouldShowTutorial,
  )

  const getUserFailed = useAppSelector(
    (state) => state.onboarding.getUserFailed,
  )

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (
      user?.isShowTutorial &&
      !isRevalidatedShouldShowTutorial &&
      !getUserFailed
    ) {
      dispatch(getUser(true))
    }
  }, [user, isRevalidatedShouldShowTutorial, getUserFailed])

  useFailed(getUserFailed, { message: "Get user's scanning info failed!" })
  return {
    isShowTutorial: user?.isShowTutorial ?? true,
    checkShowTutorialLoading: loading,
    isRevalidatedShouldShowTutorial,
  }
}

export const useIsTutorialShowed = () => {
  const isTutorialShowed = useAppSelector(
    (state) => state.onboarding.isTutorialShowed,
  )

  const dispatch = useAppDispatch()
  const updateIsTutorialShowed = (isShowed: boolean) => {
    dispatch(setIsTutorialShowed(isShowed))
  }

  return {
    isTutorialShowed,
    updateIsTutorialShowed,
  }
}

export const useTutorialBackPath = () => {
  const tutorialBackPath = useAppSelector(
    (state) => state.onboarding.tutorialBackPath,
  )

  const dispatch = useAppDispatch()
  const updateTutorialBackPath = (path: string) => {
    dispatch(setTutorialBackPath(path))
  }

  return {tutorialBackPath, updateTutorialBackPath}
}

export const useDisplayWeightChangedNotification = () => {
  const shouldShowWeightChangedNotification = useAppSelector(
    (state) => state.onboarding.shouldShowWeightChangedNotification,
  )

  const goal = useAppSelector((state) => state.onboarding.goal)
  const caloriesPerDay = goal?.caloriesPerDay
  const newCaloriesPerDay = goal?.newCaloriesPerDay

  const dispatch = useAppDispatch()

  const handleTurnOffWeightChangedNotification = () => {
    dispatch(turnOffWeightChangeNotification())
  }

  const isDisplayWeightChangedNotification =
    shouldShowWeightChangedNotification &&
    isNotNullNotUndefined(newCaloriesPerDay) &&
    isNotNullNotUndefined(caloriesPerDay) &&
    caloriesPerDay !== newCaloriesPerDay

  return {
    isDisplayWeightChangedNotification,
    caloriesPerDay,
    newCaloriesPerDay,
    handleTurnOffWeightChangedNotification,
  }
}
